import React, { useState, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { GitCompare, Clock, ArrowUp, ArrowDown, Minus, Layers } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Legend,
} from 'recharts';
import type { QuarterData, TimeGranularity, AnalysisConfig } from '@/types/piekanalyse';
import { aggregateQuartersToHourly } from '@/lib/piekvermoigenParser';

interface DayCompareDialogProps {
  open: boolean;
  onClose: () => void;
  rawData: Record<string, { quarters: QuarterData[] }> | undefined;
  config: AnalysisConfig;
  initialDateA?: string;
}

export const DayCompareDialog: React.FC<DayCompareDialogProps> = ({
  open,
  onClose,
  rawData,
  config,
  initialDateA,
}) => {
  const availableDates = useMemo(() => {
    if (!rawData) return [];
    return Object.keys(rawData).sort();
  }, [rawData]);

  const [dateA, setDateA] = useState<string>(initialDateA || availableDates[0] || '');
  const [dateB, setDateB] = useState<string>(availableDates[1] || availableDates[0] || '');
  const [granularity, setGranularity] = useState<TimeGranularity>('hour');
  const [overlayMode, setOverlayMode] = useState(true);

  React.useEffect(() => {
    if (initialDateA) {
      setDateA(initialDateA);
    }
  }, [initialDateA]);

  const dataA = useMemo(() => {
    if (!rawData || !dateA) return null;
    return rawData[dateA];
  }, [rawData, dateA]);

  const dataB = useMemo(() => {
    if (!rawData || !dateB) return null;
    return rawData[dateB];
  }, [rawData, dateB]);

  const chartDataA = useMemo(() => {
    if (!dataA) return [];
    if (granularity === 'hour') {
      return aggregateQuartersToHourly(dataA.quarters).map(h => ({
        label: h.hourLabel,
        kWp: h.maxKWp,
      }));
    }
    return dataA.quarters.map(q => ({ label: q.time, kWp: q.kWp }));
  }, [dataA, granularity]);

  const chartDataB = useMemo(() => {
    if (!dataB) return [];
    if (granularity === 'hour') {
      return aggregateQuartersToHourly(dataB.quarters).map(h => ({
        label: h.hourLabel,
        kWp: h.maxKWp,
      }));
    }
    return dataB.quarters.map(q => ({ label: q.time, kWp: q.kWp }));
  }, [dataB, granularity]);

  // Merged data for overlay mode
  const overlayData = useMemo(() => {
    const labels = granularity === 'hour' 
      ? Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`)
      : Array.from({ length: 96 }, (_, i) => {
          const h = Math.floor(i / 4);
          const m = (i % 4) * 15;
          return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        });

    const dataAMap = new Map(chartDataA.map(d => [d.label, d.kWp]));
    const dataBMap = new Map(chartDataB.map(d => [d.label, d.kWp]));

    return labels.map(label => ({
      label,
      dagA: dataAMap.get(label) || 0,
      dagB: dataBMap.get(label) || 0,
    }));
  }, [chartDataA, chartDataB, granularity]);

  // Statistics
  const statsA = useMemo(() => {
    if (!dataA) return { peak: 0, avg: 0, exceedances: 0 };
    const values = dataA.quarters.map(q => q.kWp);
    const peak = Math.max(...values);
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    const exceedances = config.contractedCapacity
      ? dataA.quarters.filter(q => q.kWp > config.contractedCapacity).length
      : 0;
    return { peak: Math.round(peak * 10) / 10, avg: Math.round(avg * 10) / 10, exceedances };
  }, [dataA, config.contractedCapacity]);

  const statsB = useMemo(() => {
    if (!dataB) return { peak: 0, avg: 0, exceedances: 0 };
    const values = dataB.quarters.map(q => q.kWp);
    const peak = Math.max(...values);
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    const exceedances = config.contractedCapacity
      ? dataB.quarters.filter(q => q.kWp > config.contractedCapacity).length
      : 0;
    return { peak: Math.round(peak * 10) / 10, avg: Math.round(avg * 10) / 10, exceedances };
  }, [dataB, config.contractedCapacity]);

  const maxValue = useMemo(() => {
    const values = [
      ...chartDataA.map(d => d.kWp),
      ...chartDataB.map(d => d.kWp),
      config.contractedCapacity || 0,
    ];
    const max = Math.max(...values) * 1.15;
    return Math.ceil(max / 50) * 50;
  }, [chartDataA, chartDataB, config.contractedCapacity]);

  const getDiffIndicator = (a: number, b: number) => {
    if (a === b) return <Minus className="h-3 w-3 text-muted-foreground" />;
    const pct = ((b - a) / a * 100).toFixed(1);
    if (b > a) return <span className="flex items-center gap-1 text-destructive"><ArrowUp className="h-3 w-3" />+{pct}%</span>;
    return <span className="flex items-center gap-1 text-green-600"><ArrowDown className="h-3 w-3" />{pct}%</span>;
  };

  const tickInterval = granularity === 'quarter' ? 7 : 1;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
        <p className="font-semibold text-foreground mb-2">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: <span className="font-medium">{entry.value?.toFixed(1)} kWp</span>
          </p>
        ))}
      </div>
    );
  };

  if (!rawData || availableDates.length < 2) {
    return (
      <Dialog open={open} onOpenChange={() => onClose()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="h-5 w-5" />
              Dagen Vergelijken
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-12 text-muted-foreground">
            <p>Minimaal 2 dagen data nodig voor vergelijking</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompare className="h-5 w-5" />
            Dagen Vergelijken
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[calc(90vh-120px)] pr-4">
          <div className="space-y-6">
            {/* Controls */}
            <div className="flex flex-wrap items-center gap-4 p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-2">
                <Badge variant="default" className="bg-primary">A</Badge>
                <Select value={dateA} onValueChange={setDateA}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Selecteer dag A" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableDates.map(date => (
                      <SelectItem key={date} value={date}>
                        {format(parseISO(date), 'd MMMM yyyy', { locale: nl })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Badge variant="secondary">B</Badge>
                <Select value={dateB} onValueChange={setDateB}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Selecteer dag B" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableDates.map(date => (
                      <SelectItem key={date} value={date}>
                        {format(parseISO(date), 'd MMMM yyyy', { locale: nl })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2 ml-auto">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <Select value={granularity} onValueChange={(v) => setGranularity(v as TimeGranularity)}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quarter">Kwartier</SelectItem>
                    <SelectItem value="hour">Uur</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  id="overlay"
                  checked={overlayMode}
                  onCheckedChange={setOverlayMode}
                />
                <Label htmlFor="overlay" className="flex items-center gap-1">
                  <Layers className="h-4 w-4" />
                  Overlay
                </Label>
              </div>
            </div>

            {/* Chart(s) */}
            {overlayMode ? (
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={overlayData} margin={{ top: 10, right: 30, left: 20, bottom: 50 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                      interval={tickInterval}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis
                      domain={[0, maxValue]}
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    
                    {config.contractedCapacity && (
                      <ReferenceLine
                        y={config.contractedCapacity}
                        stroke="hsl(var(--destructive))"
                        strokeDasharray="5 5"
                        strokeWidth={2}
                      />
                    )}
                    
                    <Line
                      type="monotone"
                      dataKey="dagA"
                      name={`Dag A: ${dateA && format(parseISO(dateA), 'd MMM', { locale: nl })}`}
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="dagB"
                      name={`Dag B: ${dateB && format(parseISO(dateB), 'd MMM', { locale: nl })}`}
                      stroke="hsl(221 83% 53%)"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="h-[300px] border rounded-lg p-4">
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                    <Badge variant="default" className="bg-primary">A</Badge>
                    {dateA && format(parseISO(dateA), 'd MMMM yyyy', { locale: nl })}
                  </h4>
                  <ResponsiveContainer width="100%" height="85%">
                    <LineChart data={chartDataA}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                      <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={tickInterval} />
                      <YAxis domain={[0, maxValue]} tick={{ fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      {config.contractedCapacity && (
                        <ReferenceLine y={config.contractedCapacity} stroke="hsl(var(--destructive))" strokeDasharray="5 5" />
                      )}
                      <Line type="monotone" dataKey="kWp" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="h-[300px] border rounded-lg p-4">
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                    <Badge variant="secondary">B</Badge>
                    {dateB && format(parseISO(dateB), 'd MMMM yyyy', { locale: nl })}
                  </h4>
                  <ResponsiveContainer width="100%" height="85%">
                    <LineChart data={chartDataB}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                      <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={tickInterval} />
                      <YAxis domain={[0, maxValue]} tick={{ fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      {config.contractedCapacity && (
                        <ReferenceLine y={config.contractedCapacity} stroke="hsl(var(--destructive))" strokeDasharray="5 5" />
                      )}
                      <Line type="monotone" dataKey="kWp" stroke="hsl(221 83% 53%)" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Comparison Table */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Metriek</TableHead>
                  <TableHead className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Badge variant="default" className="bg-primary">A</Badge>
                      {dateA && format(parseISO(dateA), 'd MMM', { locale: nl })}
                    </div>
                  </TableHead>
                  <TableHead className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Badge variant="secondary">B</Badge>
                      {dateB && format(parseISO(dateB), 'd MMM', { locale: nl })}
                    </div>
                  </TableHead>
                  <TableHead className="text-center">Verschil</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">Hoogste piek</TableCell>
                  <TableCell className="text-center">{statsA.peak} kWp</TableCell>
                  <TableCell className="text-center">{statsB.peak} kWp</TableCell>
                  <TableCell className="text-center">{getDiffIndicator(statsA.peak, statsB.peak)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Gemiddeld</TableCell>
                  <TableCell className="text-center">{statsA.avg} kWp</TableCell>
                  <TableCell className="text-center">{statsB.avg} kWp</TableCell>
                  <TableCell className="text-center">{getDiffIndicator(statsA.avg, statsB.avg)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Overschrijdingen</TableCell>
                  <TableCell className="text-center">{statsA.exceedances}</TableCell>
                  <TableCell className="text-center">{statsB.exceedances}</TableCell>
                  <TableCell className="text-center">
                    {statsA.exceedances === statsB.exceedances ? (
                      <Minus className="h-3 w-3 mx-auto text-muted-foreground" />
                    ) : statsB.exceedances > statsA.exceedances ? (
                      <span className="text-destructive">+{statsB.exceedances - statsA.exceedances}</span>
                    ) : (
                      <span className="text-green-600">{statsB.exceedances - statsA.exceedances}</span>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default DayCompareDialog;
